import type { Client } from '@lastrouter/contracts';
import {
  ArrowLeft,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  Download,
  Field,
  Input,
  Plus,
  Tabs,
  TabsContent,
  TabsList,
  Switch,
  TabsTrigger,
  Textarea,
  Tooltip,
} from '@lastrouter/ui';
import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { errorMessage, useApiMutation, useApiQuery } from '../../api/hooks.js';
import { AdminOnly } from '../../app/shell.js';
import { ErrorState, linkClass, PageHeader, QueryState, StatusBadge, useUnsavedWarning } from '../../components/common.js';
import type { AppsSearch } from '../assets/logic.js';
import { AddAppsDialog, AppsList, SyncButton } from './apps.js';

type SetAppsSearch = (patch: Partial<AppsSearch>, replace?: boolean) => void;

export type ClientTab = 'apps' | 'context';

function AppsTab({ client, search, setSearch }: { client: Client; search: AppsSearch; setSearch: SetAppsSearch }) {
  const { t } = useTranslation();
  const [adding, setAdding] = useState(false);
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap justify-end gap-2">
        <SyncButton />
        <Button variant="primary" onClick={() => setAdding(true)}>
          <Plus />
          {t('clients.addApp')}
        </Button>
      </div>
      <AppsList search={search} setSearch={setSearch} fixedClient={client.id} />
      {adding ? <AddAppsDialog clientId={client.id} clientName={client.name} onClose={() => setAdding(false)} /> : null}
    </div>
  );
}

function CompanyContextTab({ client }: { client: Client }) {
  const { t } = useTranslation();
  const save = useApiMutation('updateClient', ['client', 'clients']);
  const initial = { name: client.name, context_md: client.context_md, privacy_mode: client.privacy_mode };
  const [form, setForm] = useState(initial);
  // Recomeça do servidor quando o cliente muda lá (salvo aqui ou por outra pessoa).
  useEffect(
    () => setForm({ name: client.name, context_md: client.context_md, privacy_mode: client.privacy_mode }),
    [client.updated_at, client.name, client.context_md, client.privacy_mode],
  );
  const dirty = form.name !== initial.name || form.context_md !== initial.context_md || form.privacy_mode !== initial.privacy_mode;
  useUnsavedWarning(dirty);

  function submit(e: FormEvent) {
    e.preventDefault();
    save.mutate(
      { params: { id: client.id }, body: { name: form.name.trim(), context_md: form.context_md, privacy_mode: form.privacy_mode } },
      { onSuccess: () => toast.success(t('clients.saved')), onError: (err) => toast.error(errorMessage(t, err)) },
    );
  }

  return (
    <Card>
      <CardContent>
        <form className="flex flex-col gap-4" onSubmit={submit}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field id="empresa-nome" label={t('clients.name')}>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} maxLength={120} required />
            </Field>
          </div>
          <div className="flex items-start gap-3 rounded-[var(--lr-radius-md)] border border-border p-3">
            <Switch
              id="client-privacy"
              checked={form.privacy_mode}
              onCheckedChange={(v) => setForm({ ...form, privacy_mode: v })}
              aria-describedby="client-privacy-help"
            />
            <div className="flex flex-col gap-0.5">
              <label htmlFor="client-privacy" className="font-medium text-fg">
                {t('clients.privacy')}
              </label>
              <p id="client-privacy-help" className="text-[length:var(--lr-text-sm)] text-fg-muted">
                {t('clients.privacyHelp')}
              </p>
            </div>
          </div>
          <Field id="empresa-contexto" label={t('clients.context')} hint={t('clients.contextHelp')}>
            <Textarea rows={18} className="font-mono" value={form.context_md} onChange={(e) => setForm({ ...form, context_md: e.target.value })} maxLength={20_000} />
          </Field>
          <div className="flex flex-wrap items-center gap-3">
            <Button type="submit" variant="primary" disabled={!dirty || save.isPending || !form.name.trim()}>
              {t('common.save')}
            </Button>
            {dirty ? <StatusBadge tone="warning">{t('common.unsavedBadge')}</StatusBadge> : null}
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function DeleteDialog({ client, onClose }: { client: Client; onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const del = useApiMutation('deleteClient', ['clients', 'assets', 'searchAssets']);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title={t('clients.delete')} description={t('clients.deleteConfirm', { name: client.name })} closeLabel={t('common.close')}>
        {del.error ? <ErrorState error={del.error} /> : null}
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="danger"
            disabled={del.isPending}
            onClick={() =>
              del.mutate(
                { params: { id: client.id } },
                {
                  onSuccess: () => {
                    toast.success(t('clients.deleted'));
                    void navigate({ to: '/clients' });
                  },
                },
              )
            }
          >
            {t('clients.delete')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ClientPage({ id, tab, onTab, search, setSearch }: { id: string; tab: ClientTab; onTab: (t: ClientTab) => void; search: AppsSearch; setSearch: SetAppsSearch }) {
  const { t } = useTranslation();
  const q = useApiQuery('client', { params: { id } });
  const [deleting, setDeleting] = useState(false);
  return (
    <AdminOnly>
      <p className="mb-3">
        <Link to="/clients" className={`${linkClass} inline-flex items-center gap-1`}>
          <ArrowLeft className="size-4" aria-hidden="true" />
          {t('clients.back')}
        </Link>
      </p>
      <QueryState query={q}>
        {(client) => (
          <>
            <PageHeader
              title={client.name}
              actions={
                <>
                  <Tooltip content={t('clients.soon')}>
                    {/* Botão desabilitado não recebe foco nem ponteiro: o span leva o tooltip. */}
                    <span tabIndex={0} aria-label={`${t('clients.export')}: ${t('clients.soon')}`}>
                      <Button disabled tabIndex={-1}>
                        <Download />
                        {t('clients.export')}
                      </Button>
                    </span>
                  </Tooltip>
                  <Button variant="danger" onClick={() => setDeleting(true)}>
                    {t('clients.delete')}
                  </Button>
                </>
              }
            />
            <Tabs value={tab} onValueChange={(v) => onTab(v as ClientTab)}>
              <TabsList aria-label={client.name}>
                <TabsTrigger value="apps">{t('clients.tabApps')}</TabsTrigger>
                <TabsTrigger value="context">{t('clients.tabContext')}</TabsTrigger>
              </TabsList>
              <TabsContent value="apps">
                <AppsTab client={client} search={search} setSearch={setSearch} />
              </TabsContent>
              {/* Fica montado ao trocar de aba para não perder a edição local. */}
              <TabsContent value="context" forceMount className="data-[state=inactive]:hidden">
                <CompanyContextTab client={client} />
              </TabsContent>
            </Tabs>
            {deleting ? <DeleteDialog client={client} onClose={() => setDeleting(false)} /> : null}
          </>
        )}
      </QueryState>
    </AdminOnly>
  );
}
